import {inject, injectable} from "inversify";
import type {IHttpClientService} from "data/services/http";
import type {AxiosResponse} from "axios";
import {Bindings} from "data/constants/bindings";
import type {
	IArticles,
	ICategories,
	ISections,
} from "data/stores/static_content/static_content.store";
import type {IPlayer} from "data/stores/players/players.store";
import type {ISquad} from "data/stores/squads/squads.store";
import type {IChecksums} from "data/stores/checksum/checksum.store";
import {Language} from "data/enums";
import {IContest} from "data/stores/contests/contests.store";
import {SYNDICATION_PREFIX} from "data/constants";
import {IGame} from "data/stores/games/games.store";
import {IContestPlayer} from "data/stores/contest_players/contest_players.store";
import type {IRound} from "data/stores/rounds/rounds.store";

export interface IJSONProvider {
	rounds(): Promise<AxiosResponse<IRound[]>>;
	contests(): Promise<AxiosResponse<IContest[]>>;
	games(): Promise<AxiosResponse<IGame[]>>;
	players(): Promise<AxiosResponse<IPlayer[]>>;
	squads(): Promise<AxiosResponse<ISquad[]>>;
	contest_players(id: number): Promise<AxiosResponse<{players: IContestPlayer[]}>>;
	checksums(): Promise<AxiosResponse<IChecksums>>;

	translations(locale: string): Promise<AxiosResponse<Record<string, unknown>>>;
	helpCategories(locale: string): Promise<AxiosResponse<ICategories>>;
	helpSections(locale: string): Promise<AxiosResponse<ISections>>;
	helpArticles(locale: string): Promise<AxiosResponse<IArticles>>;
	helpContentInternal(lang: string): Promise<AxiosResponse<IArticles>>;
}

@injectable()
export class JSONProvider implements IJSONProvider {
	constructor(
		@inject(Bindings.JsonHTTPClient) private _jsonClient: IHttpClientService,
		@inject(Bindings.ContentJsonHTTPClient) private _contentJsonClient: IHttpClientService
	) {}
	rounds = () => this._jsonClient.get<IRound[]>(`rounds.json`);
	contests = () => this._jsonClient.get<IContest[]>(`${SYNDICATION_PREFIX}/contests.json`);
	games = () => this._jsonClient.get<IGame[]>(`games.json`);
	players = () => this._jsonClient.get<IPlayer[]>("players.json");
	squads = () => this._jsonClient.get<ISquad[]>("squads.json");
	contest_players = (id: number) =>
		this._jsonClient.get<{players: IContestPlayer[]}>(`${SYNDICATION_PREFIX}/${id}.json`);
	checksums = () => this._jsonClient.get<IChecksums>("checksums.json");
	translations = (locale: string) =>
		this._jsonClient.get<Record<string, unknown>>(`loco/${locale}.json`);

	helpCategories = (locale: Language) =>
		this._contentJsonClient.get<ICategories>(`${locale}/categories.json`);
	helpSections = (locale: Language) =>
		this._contentJsonClient.get<ISections>(`${locale}/sections.json`);
	helpArticles = (locale: Language) =>
		this._contentJsonClient.get<IArticles>(`${locale}/articles.json`);
	helpContentInternal = (lang: Language) =>
		this._jsonClient.get<IArticles>(`help_center/${lang}/articles.json`);
}
